import { useMemo, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { Alert, Button, Flex, Form, Modal } from 'antd';

import SectionMessage from 'components/SectionMessage/SectionMessage';
import { UPDATE_PRECO_FUNDING_ACCOUNT } from 'mutations/prechargeoff';
import { SIZE, STATUS } from 'utilities/constants';

import FundingAccountPicker from '../../FundingAccountPicker';
import PrecoEFTAForm from './PrecoEFTAForm';
import { getScheduledTransactions } from './precoPaymentUtils';

const PrecoChangeFundingAccountModal = ({ offer, hide, ...modalProps }) => {
  const client = useApolloClient();

  const [form] = Form.useForm();
  const [eftaForm] = Form.useForm();

  const currentFundingAccountId = offer.paymentPlan?.fundingAccount?.id ?? null;

  const [selectedFundingAccountId, setSelectedFundingAccountId] = useState(
    currentFundingAccountId,
  );
  const [selectedFundingAccountName, setSelectedFundingAccountName] =
    useState('');
  const [isAuthorizing, setIsAuthorizing] = useState(false);

  const fundingAccountLast4 = selectedFundingAccountName.slice(-4);

  // Drop `status` so it doesn't leak into the stored authorization text, which
  // JSON-stringifies this schedule.
  const schedule = useMemo(
    () =>
      getScheduledTransactions(offer.paymentPlan).map(({ amount, date }) => ({
        amount,
        date,
      })),
    [offer.paymentPlan],
  );

  const [updateFundingAccount, { loading, error, data: updateSuccess, reset }] =
    useMutation(UPDATE_PRECO_FUNDING_ACCOUNT, {
      onError: () => {},
      onCompleted: () =>
        setTimeout(() => {
          client.refetchQueries({
            include: ['GetCurrentPrecoOffer', 'AccountHistory'],
          });
          handleCancel();
        }, 2500),
    });

  const handleCancel = () => {
    eftaForm.resetFields();
    reset();
    setIsAuthorizing(false);
    setSelectedFundingAccountId(currentFundingAccountId);
    setSelectedFundingAccountName('');
    hide();
  };

  const onSelectFundingAccount = (bankAccountId, displayName) => {
    setSelectedFundingAccountId(bankAccountId);
    setSelectedFundingAccountName(displayName);
  };

  const onConfirmFundingAccount = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }
    setIsAuthorizing(true);
  };

  const onFinish = (authorizationText) => {
    updateFundingAccount({
      variables: {
        offerId: offer.offerId,
        fundingAccountId: String(selectedFundingAccountId),
        authorizationText,
      },
    });
  };

  return (
    <Modal
      {...modalProps}
      title="Change Funding Account"
      width={500}
      footer={null}
      onCancel={handleCancel}
      destroyOnHidden={true}
    >
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        {isAuthorizing ? (
          <PrecoEFTAForm
            form={eftaForm}
            onFinish={onFinish}
            fundingAccountLast4={fundingAccountLast4}
            schedule={schedule}
          >
            <Flex justify="flex-end" gap="var(--spacing-sm)">
              <Button onClick={() => setIsAuthorizing(false)}>Back</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Confirm and Update
              </Button>
            </Flex>
          </PrecoEFTAForm>
        ) : (
          <FundingAccountPicker
            form={form}
            initialValues={{ fundingAccount: selectedFundingAccountId }}
            fundingAccountId={selectedFundingAccountId}
            onSelect={onSelectFundingAccount}
          >
            <Button
              type="primary"
              disabled={selectedFundingAccountId === currentFundingAccountId}
              onClick={onConfirmFundingAccount}
            >
              Confirm Funding Account
            </Button>
          </FundingAccountPicker>
        )}
        {error && (
          <Alert
            banner
            type="error"
            title="Failed to update funding account. Please try again."
            style={{ marginTop: 'var(--spacing-sm)' }}
          />
        )}
        <SectionMessage
          cover={true}
          status={STATUS.SUCCESS}
          size={SIZE.SM}
          text="Funding account successfully updated"
          visible={!!updateSuccess}
        />
      </div>
    </Modal>
  );
};

export default PrecoChangeFundingAccountModal;
